import mapboxgl from "mapbox-gl"
import "mapbox-gl/dist/mapbox-gl.css"
import PropTypes from "prop-types"
import React, { useEffect, useRef } from "react"

import editCruiseStopLink from "./cruiseStopLink"

const CruisePlanMap = ({ stops, cruisePlanId }) => {
  const mapContainer = useRef(null)
  const map = useRef(null)
  const markers = useRef([])

  useEffect(() => {
    if (!stops.length) return

    mapboxgl.accessToken = process.env.MAPBOX_TOKEN

    // Initialize map
    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/mapbox/streets-v12",
      center: [stops[0].longitude, stops[0].latitude],
      zoom: 9,
    })

    // Add navigation controls
    map.current.addControl(new mapboxgl.NavigationControl(), "top-right")

    // Add markers for each stop
    stops.forEach((stop, index) => {
      if (!stop.latitude || !stop.longitude) return

      // Create marker element
      const el = document.createElement("div")
      el.className = "marker"
      el.innerHTML = `<div class="bg-gray-700 text-white font-bold text-sm rounded-full size-8 flex items-center justify-center">${
        index + 1
      }</div>`

      // Create popup
      const popup = new mapboxgl.Popup({ offset: 25 }).setHTML(`
        <div class="p-2">
          <h3 class="font-bold mt-0 text-lg">${stop.name}</h3>
          ${
            stop.note ? `<p class="text-sm text-gray-600">${stop.note}</p>` : ""
          }
          <a href="${editCruiseStopLink(
            stop,
            cruisePlanId
          )}" class="text-blue-600 hover:text-blue-800">Edit stop</a>
        </div>
      `)

      // Add marker to map
      const marker = new mapboxgl.Marker(el)
        .setLngLat([stop.longitude, stop.latitude])
        .setPopup(popup)
        .addTo(map.current)

      markers.current.push(marker)
    })

    // Fit bounds to show all markers
    if (markers.current.length > 1) {
      const bounds = new mapboxgl.LngLatBounds()
      markers.current.forEach((marker) => {
        bounds.extend(marker.getLngLat())
      })
      map.current.fitBounds(bounds, {
        padding: 50,
        maxZoom: 12,
      })
    }

    return () => {
      markers.current.forEach((marker) => marker.remove())
      if (map.current) map.current.remove()
    }
  }, [stops])

  return (
    <div className="relative h-full w-full">
      <div ref={mapContainer} className="absolute inset-0" />
    </div>
  )
}

CruisePlanMap.propTypes = {
  stops: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      name: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
      note: PropTypes.string,
    })
  ).isRequired,
  cruisePlanId: PropTypes.string.isRequired,
}

export default CruisePlanMap
